import { createApi } from "@reduxjs/toolkit/query/react";
import { apiRequestBaseQuery } from "../../services/rtkBaseQuery";
import { getUserDetails } from "../../services/apiRequest";
import { requestType } from "../../services/requests";

// =============================================================================
// ROYAL MOMENTUM - Agent Engagement API
// =============================================================================

// V2 helpers — `agency_id` (= selectedBrand.uid = agency_id_no_r) replaces `travel_agency_uid`.
// All three Agent Engagement tabs (Club Learn, Royal Genie, Upper Deck) are on V2.
const buildV2RequestData = (params) => ({
    user_uid: getUserDetails()?.user_details?.uid,
    country: params.country || "",
    agency_id: params.travelAgencyUid || null,
    type: params.type,
    start_date: params.startDate,
    end_date: params.endDate || null,
});

// V2 "by month" helper (year only)
const buildV2YearRequestData = (params) => {
    // startDate may be "2025", "2025-02", or "2025-01-01" — take the year segment.
    const startDate = params.startDate || "";
    const year = startDate.split("-")[0] || new Date().getFullYear().toString();
    return {
        user_uid: getUserDetails()?.user_details?.uid,
        country: params.country || "",
        agency_id: params.travelAgencyUid || null,
        year,
    };
};

export const royalMomentumAgentEngagementApi = createApi({
    reducerPath: "royalMomentumAgentEngagementApi",
    baseQuery: apiRequestBaseQuery,
    refetchOnMountOrArgChange: true,
    tagTypes: ["RoyalGenie", "ClubLearn", "UpperDeck"],
    endpoints: (builder) => ({
        // =====================================================================
        // ROYAL GENIE
        // =====================================================================

        getProTotalAgentsSignedUp: builder.query({
            query: (params) => ({
                requestType: requestType.getTotalAgentsSignedUpV2,
                data: buildV2RequestData(params),
            }),
            providesTags: ["RoyalGenie"],
        }),

        // Agents Signed Up By Month (uses year-only format)
        getProAgentsSignedUpByMonth: builder.query({
            query: (params) => ({
                requestType: requestType.getAgentsSignedUpByMonthV2,
                data: buildV2YearRequestData(params),
            }),
            providesTags: ["RoyalGenie"],
        }),

        // Proposals Generated By Month (uses year-only format)
        getProProposalsGeneratedByMonth: builder.query({
            query: (params) => ({
                requestType: requestType.getProposalsGeneratedByMonthV2,
                data: buildV2YearRequestData(params),
            }),
            providesTags: ["RoyalGenie"],
        }),

        // Total Proposals Generated
        getProTotalProposalsGenerated: builder.query({
            query: (params) => ({
                requestType: requestType.getTotalProposalsGeneratedV2,
                data: buildV2RequestData(params),
            }),
            providesTags: ["RoyalGenie"],
        }),

        // Product Mix Genie Proposals
        getProProductMixGenieProposals: builder.query({
            query: (params) => ({
                requestType: requestType.getProductMixGenieProposalsV2,
                data: buildV2RequestData(params),
            }),
            providesTags: ["RoyalGenie"],
        }),

        // Top 10 Itineraries Genie Proposals
        getProTop10ItinerariesGenieProposals: builder.query({
            query: (params) => ({
                requestType: requestType.getTop10ItinerariesGenieProposalsV2,
                data: buildV2RequestData(params),
            }),
            providesTags: ["RoyalGenie"],
        }),

        getProStoreBreakdown: builder.query({
            query: (params) => ({
                requestType: requestType.getStoreBreakdownV2,
                data: buildV2RequestData(params),
            }),
            providesTags: ["RoyalGenie"],
        }),

        // =====================================================================
        // CLUB LEARN
        // =====================================================================

        getProFocusModules: builder.query({
            query: (params) => ({
                requestType: requestType.getFocusModulesV2,
                data: buildV2YearRequestData(params),
            }),
            providesTags: ["ClubLearn"],
        }),

        getProMyRoyalGuruTotals: builder.query({
            query: (params) => ({
                requestType: requestType.getMyRoyalGuruTotalsV2,
                data: buildV2RequestData(params),
            }),
            providesTags: ["ClubLearn"],
        }),

        getProStoresWithRoyalGuru: builder.query({
            query: (params) => ({
                requestType: requestType.getStoresWithRoyalGuruV2,
                data: buildV2RequestData(params),
            }),
            providesTags: ["ClubLearn"],
        }),

        getProStoresWithoutRoyalGuru: builder.query({
            query: (params) => ({
                requestType: requestType.getStoresWithoutRoyalGuruV2,
                data: buildV2RequestData(params),
            }),
            providesTags: ["ClubLearn"],
        }),

        getProTotalModulesCompleted: builder.query({
            query: (params) => ({
                requestType: requestType.getTotalModulesCompletedV2,
                data: buildV2RequestData(params),
            }),
            providesTags: ["ClubLearn"],
        }),

        getProClubLearnModulesCompletedByMonth: builder.query({
            query: (params) => ({
                requestType: requestType.getClubLearnModulesCompletedByMonthV2,
                data: buildV2YearRequestData(params),
            }),
            providesTags: ["ClubLearn"],
        }),

        getProTopStoreGuru: builder.query({
            query: (params) => ({
                requestType: requestType.getTopStoreGuruV2,
                data: { ...buildV2RequestData(params), limit: params.limit || 100 },
            }),
            providesTags: ["ClubLearn"],
        }),

        // =====================================================================
        // UPPER DECK
        // The two totals below are Upper-Deck-scoped on the BE. The agents one used to piggyback on
        // Royal Genie's getProTotalAgentsSignedUp; it now has its own endpoint, so the two tabs no
        // longer share a request type or a cache tag.
        // Note: getProAgentsSignedUpByMonth is still shared with Royal Genie (its chart is currently
        // commented out on this tab).
        // =====================================================================

        getProUpperDeckTotalAgentsSignedUp: builder.query({
            query: (params) => ({
                requestType: requestType.getUpperDeckTotalAgentsSignedUpV2,
                data: buildV2RequestData(params),
            }),
            providesTags: ["UpperDeck"],
        }),

        getProTotalBookingsRedeemed: builder.query({
            query: (params) => ({
                requestType: requestType.getUpperDeckTotalBookingsRedeemedV2,
                data: buildV2RequestData(params),
            }),
            providesTags: ["UpperDeck"],
        }),

        getProBookingsRedeemedByMonth: builder.query({
            query: (params) => ({
                requestType: requestType.getBookingsRedeemedByMonthV2,
                data: buildV2RequestData(params),
            }),
            providesTags: ["UpperDeck"],
        }),
    }),
});

// Export hooks for usage in components
export const {
    // Royal Genie
    useGetProTotalAgentsSignedUpQuery,
    useGetProAgentsSignedUpByMonthQuery,
    useGetProProposalsGeneratedByMonthQuery,
    useGetProTotalProposalsGeneratedQuery,
    useGetProProductMixGenieProposalsQuery,
    useGetProTop10ItinerariesGenieProposalsQuery,
    useGetProStoreBreakdownQuery,
    // Club Learn
    useGetProFocusModulesQuery,
    useGetProMyRoyalGuruTotalsQuery,
    useGetProStoresWithRoyalGuruQuery,
    useGetProStoresWithoutRoyalGuruQuery,
    useGetProTotalModulesCompletedQuery,
    useGetProClubLearnModulesCompletedByMonthQuery,
    useGetProTopStoreGuruQuery,
    // Upper Deck
    useGetProUpperDeckTotalAgentsSignedUpQuery,
    useGetProTotalBookingsRedeemedQuery,
    useGetProBookingsRedeemedByMonthQuery,
} = royalMomentumAgentEngagementApi;
